import type { CardDefinition, CaseDefinition, LoadEffect, NodeDefinition } from "../domain/types/index.js";
import { CARDS } from "./cards.js";
import { CASES } from "./cases.js";
import { LOAD_TABLES } from "./loads.js";
import { ORGANIZATIONS, ORG_REWARD_POOL } from "./organizations.js";
import { listStages } from "./stages.js";
import { REWARD_POOLS } from "./rewardPools.js";

function assertUniqueIds(ids: string[], label: string): void {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) throw new Error(`${label}: duplicate id "${id}"`);
    seen.add(id);
  }
}

function validateLoadWeights(loads: readonly LoadEffect[], label: string): void {
  let total = 0;
  for (const load of loads) {
    if (load.weight <= 0) throw new Error(`${label}: non-positive weight on "${load.id}"`);
    total += load.weight;
  }
  if (total <= 0) throw new Error(`${label}: load weight total must be positive`);
}

function validateCards(cards: Record<string, CardDefinition>): void {
  assertUniqueIds(Object.keys(cards), "cards");
  for (const card of Object.values(cards)) {
    if (card.type === "worker" && (card.progress === undefined || card.progress < 0)) {
      throw new Error(`cards: worker "${card.id}" needs progress >= 0`);
    }
    if (card.type !== "worker" && !card.effect) {
      throw new Error(`cards: action "${card.id}" needs effect`);
    }
  }
}

function validateCases(cases: Record<string, CaseDefinition>): void {
  assertUniqueIds(Object.keys(cases), "cases");
  const bosses = Object.values(cases).filter((c) => c.isBoss);
  if (bosses.length < 1) throw new Error("cases: expected at least 1 boss");
  for (const c of Object.values(cases)) {
    validateLoadWeights(c.loads, `case "${c.id}"`);
  }
}

function validateNodes(nodes: Record<string, NodeDefinition>, label: string): void {
  assertUniqueIds(Object.keys(nodes), label);
  const caseIds = new Set(Object.keys(CASES));
  for (const node of Object.values(nodes)) {
    for (const nextId of node.next) {
      if (!nodes[nextId]) throw new Error(`${label}: "${node.id}" references unknown "${nextId}"`);
    }
    if (node.kind === "case") {
      if (!node.caseId) throw new Error(`${label}: case node "${node.id}" missing caseId`);
      if (!caseIds.has(node.caseId)) throw new Error(`${label}: unknown caseId "${node.caseId}"`);
      if (node.required !== undefined && (!Number.isInteger(node.required) || node.required < 1)) {
        throw new Error(`${label}: case node "${node.id}" needs an integer required >= 1`);
      }
      if (node.deadline !== undefined && (!Number.isInteger(node.deadline) || node.deadline < 1)) {
        throw new Error(`${label}: case node "${node.id}" needs an integer deadline >= 1`);
      }
    } else if (node.caseId) {
      throw new Error(`${label}: "${node.id}" (${node.kind}) must not have caseId`);
    } else if (node.required !== undefined || node.deadline !== undefined) {
      throw new Error(`${label}: "${node.id}" (${node.kind}) must not have case values`);
    }
  }
  const bossNodes = Object.values(nodes).filter((n) => n.caseId && CASES[n.caseId]?.isBoss);
  if (bossNodes.length !== 1 || bossNodes[0].next.length !== 0) {
    throw new Error(`${label}: boss must be a single terminal node`);
  }
  const cells = new Set<string>();
  for (const node of Object.values(nodes)) {
    const key = `${node.x}:${node.y}`;
    if (cells.has(key)) throw new Error(`${label}: overlapping position ${key}`);
    cells.add(key);
  }
}

function validateReachability(nodes: Record<string, NodeDefinition>): void {
  const reachable = new Set<string>();
  const stack = ["start"];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    for (const next of nodes[id]?.next ?? []) stack.push(next);
  }
  for (const id of Object.keys(nodes)) {
    if (!reachable.has(id)) throw new Error(`nodes: unreachable node "${id}"`);
  }
  if (hasCycle(nodes)) throw new Error("nodes: cycle detected in map");
}

function validateMaps(): void {
  for (const stage of listStages()) {
    const label = `nodes.${stage.id}`;
    validateNodes(stage.nodes, label);
    validateReachability(stage.nodes);
  }
}

function hasCycle(nodes: Record<string, NodeDefinition>): boolean {
  const visiting = new Set<string>();
  const done = new Set<string>();
  function dfs(id: string): boolean {
    if (done.has(id)) return false;
    if (visiting.has(id)) return true;
    visiting.add(id);
    for (const next of nodes[id]?.next ?? []) {
      if (dfs(next)) return true;
    }
    visiting.delete(id);
    done.add(id);
    return false;
  }
  return dfs("start");
}

function validateRewardPools(): void {
  const cardIds = new Set(Object.keys(CARDS));
  const poolsByStage = [
    ["default", REWARD_POOLS],
    ...listStages().flatMap((stage) => stage.rewardPools ? [[stage.id, stage.rewardPools] as const] : []),
  ];
  for (const [stageId, pools] of poolsByStage) {
    for (const [size, pool] of Object.entries(pools)) {
      if (pool.workers.length + pool.actions.length < 3) throw new Error(`rewardPools.${stageId}.${size}: needs >= 3 cards`);
      for (const id of [...pool.workers, ...pool.actions]) {
        if (!cardIds.has(id)) throw new Error(`rewardPools.${stageId}.${size}: unknown card "${id}"`);
      }
    }
  }
}

function validateOrganizations(): void {
  assertUniqueIds(Object.keys(ORGANIZATIONS), "organizations");
  for (const id of ORG_REWARD_POOL) {
    if (!(id in ORGANIZATIONS)) throw new Error(`orgRewardPool: unknown "${id}"`);
  }
  for (const stage of listStages()) {
    for (const id of stage.orgRewardPool ?? []) {
      if (!(id in ORGANIZATIONS)) throw new Error(`stage "${stage.id}" orgRewardPool: unknown "${id}"`);
    }
  }
}

function validateLoadTables(): void {
  for (const [id, loads] of Object.entries(LOAD_TABLES)) {
    validateLoadWeights(loads, `loadTable "${id}"`);
  }
}

export function validateMasterData(): void {
  validateCards(CARDS);
  validateLoadTables();
  validateCases(CASES);
  validateOrganizations();
  validateRewardPools();
  validateMaps();
}
